/**
 * Spec 021: rules for the platform owner's mobile app features page. The
 * server decides; these only shape what the page shows and sends.
 */

import { BRANDS } from '../../constants/brands';

export type AppFeatureActor = { id: string; name?: string | null; email?: string | null };

export type AppFeatureDefinition = {
  key: string;
  label: string;
  description: string;
  /** `app` (spec 021) or `environment` (spec 027); missing from an older API reads as `app`. */
  group?: string | null;
  /** The value while nothing is stored; missing reads as off. */
  defaultEnabled?: boolean | null;
};

export type AppFeatureCell = {
  brandSite: string;
  feature: string;
  enabled: boolean;
  updatedAt?: string | null;
  updatedBy?: AppFeatureActor | null;
};

export type AppFeatureBrandRow = {
  brandSite: string;
  displayName: string;
  features: AppFeatureCell[];
};

export type AppFeatureMatrix = {
  features: AppFeatureDefinition[];
  brands: AppFeatureBrandRow[];
};

export type AppFeatureChange = {
  id: string;
  brandSite: string;
  feature: string;
  oldValue: boolean;
  newValue: boolean;
  changedAt: string;
  changedBy?: AppFeatureActor | null;
};

export const STALE_MESSAGE =
  'Someone else changed this setting. The page now shows the latest value; change it again if you still want to.';

export function cellKey(brandSite: string, feature: string): string {
  return `${brandSite}:${feature}`;
}

/** The catalog default of a setting: off unless the server says otherwise. */
export function defaultEnabledOf(matrix: AppFeatureMatrix | null, feature: string): boolean {
  return Boolean(matrix?.features.find(f => f.key === feature)?.defaultEnabled);
}

/** The cell for a brand and feature; a missing one reads as the setting's default. */
export function cellOf(
  matrix: AppFeatureMatrix,
  brandSite: string,
  feature: string,
): AppFeatureCell {
  const brand = matrix.brands.find(row => row.brandSite === brandSite);
  return (
    brand?.features.find(cell => cell.feature === feature) ?? {
      brandSite,
      feature,
      enabled: defaultEnabledOf(matrix, feature),
    }
  );
}

/** Spec 021 rows of the features table (an older API sends no group). */
export function appFeatureRows(matrix: AppFeatureMatrix): AppFeatureDefinition[] {
  return matrix.features.filter(feature => (feature.group ?? 'app') === 'app');
}

/** Spec 027 rows of the environment card. */
export function environmentRows(matrix: AppFeatureMatrix): AppFeatureDefinition[] {
  return matrix.features.filter(feature => feature.group === 'environment');
}

/**
 * Spec 027 D1: the apps read these from production only, so the card is
 * editable on production (and on a development server, for local checks).
 */
export function environmentCardEditable(target: 'development' | 'staging' | 'production'): boolean {
  return target !== 'staging';
}

export const ENVIRONMENT_STAGING_NOTICE =
  'Default environment and testing mode banner are read from production by every app and staging site. Change them on the production admin page.';

/**
 * Spec 027 Story 4: what to confirm before a change, or null to save at once.
 * `nextEnabled` is the value the click would save.
 */
export function environmentConfirmText(
  feature: string,
  nextEnabled: boolean,
  brandName: string,
  site?: { brandSite: string; stagingWebHost?: string | null },
): string | null {
  if (feature === 'stagingByDefault') {
    // The website follows the app (spec 027): visitors of the brand's site go to its testing site.
    const siteMoves = site?.stagingWebHost
      ? ` ${site.brandSite} sends visitors to ${site.stagingWebHost}, except on admin pages and for people who opened ${site.brandSite}/?env=production in their browser.`
      : '';
    return nextEnabled
      ? `${brandName}'s app will connect to the testing environment: a new install opens there, an installed app moves on its next start and signs out.${siteMoves} Bookings on testing are not real.`
      : `${brandName}'s app will go back to production. An installed app moves on its next start and signs out.${site?.stagingWebHost ? ` ${site.brandSite} stops sending visitors to ${site.stagingWebHost}.` : ''}`;
  }
  if (feature === 'testingBanner' && !nextEnabled) {
    return `People on ${brandName}'s testing environment will not see the testing mode banner and may take test bookings for real ones.`;
  }
  return null;
}

/** The testing site a brand's website sends visitors to (spec 027), by its production domain. */
export function stagingWebHostOf(brandSite: string): string | null {
  return Object.values(BRANDS).find(brand => brand.brandSite === brandSite)?.stagingWebHost ?? null;
}

/** "Testing" / "Production" for the default environment cell. */
export function defaultEnvironmentLabel(stagingByDefault: boolean): string {
  return stagingByDefault ? 'Testing' : 'Production';
}

/** What a click on a cell sends: the opposite value, and the value the page showed. */
export function toggleInput(cell: AppFeatureCell) {
  return {
    brandSite: cell.brandSite,
    feature: cell.feature,
    enabled: !cell.enabled,
    expectedEnabled: cell.enabled,
  };
}

/** True when the server refused because the value changed since the page loaded. */
export function isStaleError(error: unknown): boolean {
  const graphQLErrors = (error as { graphQLErrors?: Array<{ extensions?: { code?: unknown } }> })
    ?.graphQLErrors;
  return Boolean(graphQLErrors?.some(e => e.extensions?.code === 'APP_FEATURE_STALE'));
}

/** The server's message for any other refusal, else a fallback. */
export function errorMessage(error: unknown): string {
  const message = (
    error as { graphQLErrors?: Array<{ message?: string }> } | null
  )?.graphQLErrors?.[0]?.message?.trim();
  return message || 'The setting could not be saved. Try again.';
}

export function actorName(actor?: AppFeatureActor | null): string {
  return actor?.name || actor?.email || 'Someone';
}

function brandName(matrix: AppFeatureMatrix | null, brandSite: string): string {
  return matrix?.brands.find(row => row.brandSite === brandSite)?.displayName || brandSite;
}

function featureLabel(matrix: AppFeatureMatrix | null, key: string): string {
  return matrix?.features.find(feature => feature.key === key)?.label || key;
}

/** "Autopilotee: Change theme turned on"; spec 027: "Lenvoy: Default environment set to Testing". */
export function describeChange(change: AppFeatureChange, matrix: AppFeatureMatrix | null): string {
  if (change.feature === 'stagingByDefault') {
    return `${brandName(matrix, change.brandSite)}: Default environment set to ${defaultEnvironmentLabel(change.newValue)}`;
  }
  const verb = change.newValue ? 'turned on' : 'turned off';
  return `${brandName(matrix, change.brandSite)}: ${featureLabel(matrix, change.feature)} ${verb}`;
}

/** Tooltip for a cell that was never changed. */
export function neverChangedText(enabled: boolean): string {
  return `Never changed (${enabled ? 'on' : 'off'} by default)`;
}

/** "Last changed by Pat Owner, Oct 26, 2026, 10:00 AM"; null when never changed. */
export function cellChangedText(cell: AppFeatureCell, locale?: string): string | null {
  if (!cell.updatedAt) return null;
  return `Last changed by ${actorName(cell.updatedBy)}, ${formatWhen(cell.updatedAt, locale)}`;
}

export function formatWhen(iso: string, locale?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Which server's switches the page edits; store apps read production. */
export function serverNotice(target: 'development' | 'staging' | 'production'): string {
  if (target === 'production') {
    return 'These switches apply to the apps connected to production, which includes the store apps.';
  }
  const name = target === 'staging' ? 'staging' : 'this development server';
  return `These switches apply only to apps connected to ${name}. Store apps read production; change it there.`;
}
